import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApiFehler } from '../api';
import { apiBasisUrl } from '../App';
import { abmeldungFaellig, ABMELDUNG_NACH_MS } from '../bediener';
import { neueVorgangsId } from '../bonierung';
import {
  aktualisiereGeraet,
  entferneEinrichtung,
  letzteAktivitaet,
  vermerkeAktivitaet,
  istGeraeteschluessel,
  ladeGeraet,
  ladeLagerAnmeldung,
  ladeSchluessel,
  lagerKontext,
  listenSchluessel,
  oberflaechenRechte,
  speichereEinrichtung,
  speichereLagerAnmeldung,
  type LagerAnmeldung,
  standAusLaeufen,
} from '../handterminal';
import {
  ladeErfassung,
  leseMenge,
  listeNachUebergabe,
  mengeSetzen,
  mitKennung,
  bezugBeschriftung,
  pruefeCode,
  pruefzifferEinwand,
  rundeMenge,
  scanHinzufuegen,
  speichereErfassung,
  zeileEntfernen,
  fehlerZurueckEinmal,
  type Erfassung,
  type ErfassungsArt,
  ankerAus,
  inventurZiel,
  type InventurStand,
  type LaufAnker,
} from '../lager';
import {
  erstelleAuftrag,
  fehlerzeilen,
  type AuftragsInhalt,
  type LaufErgebnis,
  type Sender,
} from '../lager-warteschlange';
import {
  LagergeraetApi,
  type BestandZeile,
  type GeraeteArtikel,
  type GeraeteBewegung,
  type GeraeteInventurlauf,
  type GeraetSelbstauskunft,
  type LagerBediener,
} from '../lagergeraet-api';
import { formatiereEuro } from '../warenkorb';
import { BedienerAnmeldung } from './BedienerAnmeldung';
import { KameraScanner } from './KameraScanner';
import {
  LagerBuchungen,
  useLagerWarteschlange,
  listeBearbeitbar,
  useListenSperre,
  zaehleAuftraege,
} from './LagerBuchungen';
import { Vorschlaege, type VorschlagZeile } from './Lager';

const ZUGANG_UNGUELTIG =
  'Der Zugang dieses Geräts ist ungültig oder widerrufen. Buchungen bleiben gespeichert, bis das Gerät neu eingerichtet ist.';

type Ansicht = 'scannen' | 'suche' | 'bestand' | 'buchungen' | 'neu';
type Modus = 'auskunft' | ErfassungsArt;

function mengeText(m: number): string {
  return String(rundeMenge(m)).replace('.', ',');
}

function fehlertext(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/**
 * Handterminal-Ansicht (Android, Hochformat): mobiles Lagergerät ohne Verkaufs- oder
 * Zahlungsfunktion. Zugang über den Lagergeräte-Schlüssel aus dem Backoffice
 * (Seite „Handterminals“), Bedieneranmeldung nach den Rechten „Lager buchen“ und
 * „Artikel bearbeiten“. Start mit `?handterminal=1`.
 */
export function Handterminal() {
  const [schluessel, setSchluessel] = useState(() => ladeSchluessel(localStorage));
  const [geraet, setGeraet] = useState<GeraetSelbstauskunft | null>(() => ladeGeraet(localStorage));
  const unternehmerId = geraet?.geraet.unternehmerId ?? null;
  const [anmeldung, setAnmeldung] = useState<LagerAnmeldung | null>(() =>
    unternehmerId === null ? null : ladeLagerAnmeldung(sessionStorage, unternehmerId),
  );
  const anmeldungRef = useRef(anmeldung);
  anmeldungRef.current = anmeldung;

  // Client fest an Schlüssel und Betrieb gebunden: ein noch laufender Sendelauf eines
  // früheren Zugangs kann so nie mit einem neuen Schlüssel an einen anderen Betrieb senden.
  const api = useMemo(() => {
    if (schluessel === null || unternehmerId === null) {
      return null;
    }
    return new LagergeraetApi({
      basisUrl: apiBasisUrl(),
      schluessel: () => schluessel,
      unternehmerId: () => unternehmerId,
      bedienerSitzung: () => {
        const a = anmeldungRef.current;
        return a !== null && a.unternehmerId === unternehmerId ? a.sitzung : null;
      },
    });
  }, [schluessel, unternehmerId]);
  // Gerät in einem anderen Tab abgemeldet oder neu eingerichtet: hier sofort übernehmen,
  // damit kein Tab mit dem alten Schlüssel weiterbucht.
  useEffect(() => {
    const geaendert = (e: StorageEvent): void => {
      if (
        e.storageArea !== localStorage ||
        (e.key !== null && !e.key.startsWith('bilanx.handterminal.'))
      ) {
        return;
      }
      setSchluessel(ladeSchluessel(localStorage));
      setGeraet(ladeGeraet(localStorage));
    };
    window.addEventListener('storage', geaendert);
    return () => {
      window.removeEventListener('storage', geaendert);
    };
  }, []);

  // Stabile Rückrufe: das Gerät lädt seine Selbstauskunft in einem Effekt nach.
  const beiAnmeldung = useCallback((a: LagerAnmeldung | null) => {
    speichereLagerAnmeldung(sessionStorage, a);
    setAnmeldung(a);
  }, []);
  const beiGeraet = useCallback((g: GeraetSelbstauskunft) => {
    aktualisiereGeraet(localStorage, g);
    setGeraet(g);
  }, []);

  if (schluessel === null || geraet === null || api === null) {
    return (
      <GeraetEinrichten
        onEingerichtet={(s, g) => {
          speichereEinrichtung(localStorage, s, g);
          setSchluessel(s);
          setGeraet(g);
          setAnmeldung(null);
        }}
      />
    );
  }
  return (
    <Lagergeraet
      key={`${geraet.geraet.unternehmerId}|${String(geraet.geraet.id)}`}
      api={api}
      geraet={geraet}
      anmeldung={anmeldung}
      onAnmeldung={beiAnmeldung}
      onGeraet={beiGeraet}
      onAbmelden={() => {
        entferneEinrichtung(localStorage);
        speichereLagerAnmeldung(sessionStorage, null);
        setSchluessel(null);
        setGeraet(null);
        setAnmeldung(null);
      }}
    />
  );
}

/** Einrichtung: Schlüssel eintragen (Scanner, Tastatur oder Einfügen), am Server prüfen. */
function GeraetEinrichten(props: {
  onEingerichtet: (schluessel: string, geraet: GeraetSelbstauskunft) => void;
}) {
  const [eingabe, setEingabe] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function pruefen(): Promise<void> {
    const s = eingabe.trim();
    if (!istGeraeteschluessel(s)) {
      setFehler('Das ist kein Lagergeräte-Schlüssel (beginnt mit „lagergeraet_“).');
      return;
    }
    setLaeuft(true);
    setFehler(null);
    try {
      const probe = new LagergeraetApi({
        basisUrl: apiBasisUrl(),
        schluessel: () => s,
        unternehmerId: () => null,
        bedienerSitzung: () => null,
      });
      const g = await probe.ich();
      if (!g.module.lager) {
        setFehler('Für diesen Betrieb ist das Modul „Lager“ nicht gebucht.');
        return;
      }
      props.onEingerichtet(s, g);
    } catch (e: unknown) {
      setFehler(
        e instanceof ApiFehler && e.status === 401
          ? 'Schlüssel ungültig oder widerrufen.'
          : `Prüfung nicht möglich: ${fehlertext(e)}`,
      );
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <main className="handterminal">
      <div className="karte">
        <h2>Handterminal einrichten</h2>
        <p className="hinweis">
          Im Backoffice unter „Handterminals“ einen Zugang erstellen und den Schlüssel hier
          eintragen. Das Gerät kann damit nur Lagerfunktionen nutzen – kein Verkauf, keine Belege,
          keine Zahlungen.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void pruefen();
          }}
        >
          <label>
            Lagergeräte-Schlüssel
            <input
              value={eingabe}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              placeholder="lagergeraet_…"
              onChange={(e) => {
                setEingabe(e.target.value);
              }}
            />
          </label>
          <div className="zeile">
            <button type="submit" className="taste" disabled={laeuft || eingabe.trim() === ''}>
              Prüfen und speichern
            </button>
          </div>
        </form>
        {fehler !== null && <p className="fehler">{fehler}</p>}
      </div>
    </main>
  );
}

/** So lange gilt ein geladener Laufstand als Anker für den ersten Scan einer neuen Liste. */
const ANKER_GUELTIG_MS = 10 * 60_000;

function Lagergeraet(props: {
  api: LagergeraetApi;
  geraet: GeraetSelbstauskunft;
  anmeldung: LagerAnmeldung | null;
  onAnmeldung: (a: LagerAnmeldung | null) => void;
  onGeraet: (g: GeraetSelbstauskunft) => void;
  onAbmelden: () => void;
}) {
  const { api, geraet, anmeldung, onAnmeldung, onGeraet } = props;
  const unternehmerId = geraet.geraet.unternehmerId;
  const kontext = lagerKontext(unternehmerId);
  const [ansicht, setAnsicht] = useState<Ansicht>('scannen');
  const [online, setOnline] = useState(() => navigator.onLine);
  const [zugangFehler, setZugangFehler] = useState<string | null>(null);
  const [bediener, setBediener] = useState<LagerBediener[] | null>(null);
  const [verwaltet, setVerwaltet] = useState(geraet.module.bediener);
  const [anmeldeFehler, setAnmeldeFehler] = useState<string | null>(null);
  const [anmeldungLaeuft, setAnmeldungLaeuft] = useState(false);
  const [vorschlaege, setVorschlaege] = useState<VorschlagZeile[]>([]);
  const [gruppen, setGruppen] = useState<string[]>([]);
  // Offener Inventurlauf: Inventur-Listen gehen dann in die Zählung des Laufs. Der Stand gilt
  // nur, solange er frisch ist – ein unbekannter Stand ist nie „kein Lauf“ (sonst landete eine
  // Regalzählung als freie, absolute Inventur im Bestand).
  const [lauf, setLauf] = useState<GeraeteInventurlauf | null>(null);
  // Zuletzt geladener Laufstand (für den Laufanker neuer Listen nur, solange er frisch ist)
  const standCache = useRef<{ stand: InventurStand; am: number } | null>(null);
  // Laufstand frisch vom Server; null ohne Verbindung (dann keine Inventur übergeben)
  const laufHistorie = useCallback(async (): Promise<InventurStand | null> => {
    try {
      const laeufe = await api.inventurlaeufe();
      const stand = standAusLaeufen(laeufe);
      standCache.current = { stand, am: Date.now() };
      setLauf(laeufe.find((l) => l.status === 'offen') ?? null);
      return stand;
    } catch {
      return null;
    }
  }, [api]);
  const laufLaden = useCallback(async (): Promise<void> => {
    await laufHistorie();
  }, [laufHistorie]);
  const ankerFuerNeueListe = useCallback((): LaufAnker | undefined => {
    const c = standCache.current;
    const alter = c === null ? -1 : Date.now() - c.am;
    return c !== null && alter >= 0 && alter <= ANKER_GUELTIG_MS ? ankerAus(c.stand) : undefined;
  }, []);
  useEffect(() => {
    void laufLaden();
    const t = setInterval(() => {
      void laufLaden();
    }, 60_000);
    return () => {
      clearInterval(t);
    };
  }, [laufLaden, online]);
  const [gruppenUst, setGruppenUst] = useState<Record<string, string | null>>({});
  const rechte = oberflaechenRechte(verwaltet, anmeldung);

  const vorschlaegeLaden = useCallback(async () => {
    try {
      const v = await api.vorschlaege();
      setVorschlaege(v.vorschlaege);
      setGruppen(v.gruppen);
      setGruppenUst(v.gruppenSteuersatz);
    } catch {
      // offline – Liste bleibt
    }
  }, [api]);

  const sende = useMemo<Sender>(() => (k) => api.erfassung(k), [api]);
  const nachLauf = useCallback(
    (r: LaufErgebnis) => {
      if (r.gebucht > 0) {
        void vorschlaegeLaden();
      }
      if (r.angehalten === 'zugang') {
        setZugangFehler(ZUGANG_UNGUELTIG);
      }
    },
    [vorschlaegeLaden],
  );
  const schlange = useLagerWarteschlange(kontext, sende, nachLauf);
  const { freigeben } = schlange;
  // Listensperre auf Geräteebene: gilt auch, solange „Buchungen“ oder „Suche“ offen sind.
  const [ladeVersion, setLadeVersion] = useState(0);
  const listenSperre = useListenSperre(listenSchluessel(unternehmerId), () => {
    setLadeVersion((v) => v + 1);
  });
  // Ohne Web Locks: Änderungen anderer Tabs übernehmen (zuletzt geschrieben gilt).
  useEffect(() => {
    if (listenSperre !== 'unbekannt') {
      return undefined;
    }
    const schluessel = `bilanx.erfassung.${listenSchluessel(unternehmerId)}`;
    const geaendert = (e: StorageEvent): void => {
      if (e.key === schluessel) {
        setLadeVersion((v) => v + 1);
      }
    };
    window.addEventListener('storage', geaendert);
    return () => {
      window.removeEventListener('storage', geaendert);
    };
  }, [listenSperre, unternehmerId]);
  const zaehler = zaehleAuftraege(schlange.auftraege);
  /** Wegen eines früheren 401 wartende Aufträge einmal je Einrichtung freigeben. */
  const zugangFreigegeben = useRef(false);

  // Selbstauskunft, Bediener und Vorschläge laden; Zugang widerrufen → Hinweis
  const laden = useCallback(async () => {
    try {
      const g = await api.ich();
      onGeraet(g);
      setZugangFehler(null);
      if (!zugangFreigegeben.current) {
        // Gültiger (neuer) Schlüssel: Aufträge, die wegen eines 401 warten, wieder senden
        zugangFreigegeben.current = true;
        void freigeben(['zugang']);
      }
      const b = await api.bediener();
      setBediener(b.bediener);
      setVerwaltet(b.verwaltet);
    } catch (e: unknown) {
      if (e instanceof ApiFehler && e.status === 401) {
        setZugangFehler(ZUGANG_UNGUELTIG);
      }
    }
  }, [api, onGeraet, freigeben]);
  useEffect(() => {
    void laden();
    void vorschlaegeLaden();
  }, [laden, vorschlaegeLaden]);

  useEffect(() => {
    const an = (): void => {
      setOnline(true);
    };
    const aus = (): void => {
      setOnline(false);
    };
    window.addEventListener('online', an);
    window.addEventListener('offline', aus);
    return () => {
      window.removeEventListener('online', an);
      window.removeEventListener('offline', aus);
    };
  }, []);

  // Abmeldung nach 15 Minuten ohne Bedienung (geteiltes Gerät)
  useEffect(() => {
    if (anmeldung === null) {
      return undefined;
    }
    // Startwert aus der Sitzung: Neuladen nach langer Ruhe setzt die Uhr nicht zurück.
    let letzte = letzteAktivitaet(sessionStorage) ?? Date.now();
    const aktiv = (): void => {
      letzte = Date.now();
    };
    const ereignisse = ['pointerdown', 'keydown', 'touchstart'] as const;
    for (const e of ereignisse) {
      window.addEventListener(e, aktiv, { passive: true });
    }
    const pruefe = (): void => {
      if (abmeldungFaellig(letzte, Date.now(), ABMELDUNG_NACH_MS)) {
        onAnmeldung(null);
      } else {
        vermerkeAktivitaet(sessionStorage, letzte);
      }
    };
    pruefe();
    const timer = setInterval(pruefe, 30_000);
    return () => {
      clearInterval(timer);
      for (const e of ereignisse) {
        window.removeEventListener(e, aktiv);
      }
    };
  }, [anmeldung, onAnmeldung]);

  // Neue Anmeldung: auf Recht wartende Buchungen jetzt senden
  useEffect(() => {
    if (anmeldung !== null) {
      void freigeben(['recht']);
    }
  }, [anmeldung, freigeben]);

  async function anmelden(b: LagerBediener, pin: string | undefined): Promise<void> {
    setAnmeldungLaeuft(true);
    setAnmeldeFehler(null);
    try {
      const r = await api.bedienerAnmelden(b.id, pin);
      onAnmeldung({
        id: r.bediener.id,
        name: r.bediener.name,
        rechte: {
          lager: r.bediener.rechte.lager === true,
          artikel: r.bediener.rechte.artikel === true,
        },
        sitzung: r.sitzung,
        unternehmerId,
      });
    } catch (e: unknown) {
      setAnmeldeFehler(fehlertext(e));
    } finally {
      setAnmeldungLaeuft(false);
    }
  }

  const brauchtAnmeldung = verwaltet && anmeldung === null;

  return (
    <div className="handterminal">
      <header className="kopf handterminal-kopf">
        <span className="marke" aria-hidden="true">
          L
        </span>
        <div className="handterminal-titel">
          <strong>{geraet.geraet.name}</strong>
          <span className="hinweis">{geraet.unternehmer?.name ?? unternehmerId}</span>
        </div>
        <span className={`verbindung ${online ? 'online' : 'offline'}`}>
          {online ? 'online' : 'offline'}
        </span>
        {anmeldung !== null && (
          <button
            type="button"
            className="taste zweit klein"
            onClick={() => {
              onAnmeldung(null);
            }}
          >
            {anmeldung.name} · Abmelden
          </button>
        )}
      </header>
      {zugangFehler !== null && (
        <div className="karte">
          <p className="fehler">{zugangFehler}</p>
          <button
            type="button"
            className="taste zweit"
            onClick={() => {
              if (
                zaehler.ausstehend === 0 ||
                window.confirm(
                  `${String(zaehler.ausstehend)} Buchung(en) sind noch nicht gesendet. Sie bleiben auf dem Gerät und werden nach der neuen Einrichtung (gleicher Betrieb) gesendet. Jetzt neu einrichten?`,
                )
              ) {
                props.onAbmelden();
              }
            }}
          >
            Gerät neu einrichten
          </button>
        </div>
      )}
      {brauchtAnmeldung ? (
        <BedienerAnmeldung
          seite
          bediener={bediener ?? []}
          laeuft={anmeldungLaeuft}
          fehler={anmeldeFehler}
          hinweis={
            bediener === null
              ? 'Bediener werden geladen …'
              : 'Zum Arbeiten anmelden – nur Bediener mit Lagerrecht können buchen.'
          }
          onAnmelden={(b, pin) => void anmelden(b, pin)}
        />
      ) : (
        <>
          <nav className="reiter handterminal-reiter" aria-label="Ansicht">
            {(
              [
                ['scannen', 'Scannen'],
                ['suche', 'Suche'],
                ['bestand', 'Bestand'],
                [
                  'buchungen',
                  `Buchungen${zaehler.ausstehend + zaehler.konflikt > 0 ? ` (${String(zaehler.ausstehend + zaehler.konflikt)})` : ''}`,
                ],
                ['neu', `Neu${vorschlaege.length > 0 ? ` (${String(vorschlaege.length)})` : ''}`],
              ] as const
            ).map(([id, text]) => (
              <button
                key={id}
                type="button"
                className={ansicht === id ? 'aktiv' : ''}
                aria-current={ansicht === id ? 'page' : undefined}
                onClick={() => {
                  setAnsicht(id);
                }}
              >
                {text}
              </button>
            ))}
          </nav>
          <main className="handterminal-inhalt">
            {zaehler.konflikt > 0 && ansicht !== 'buchungen' && (
              <p className="fehler">
                {zaehler.konflikt} Buchung(en) im Konflikt – siehe „Buchungen“.
              </p>
            )}
            {ansicht === 'scannen' && (
              <ScanAnsicht
                api={api}
                unternehmerId={unternehmerId}
                online={online}
                darfBuchen={rechte.buchen}
                bediener={anmeldung?.name ?? null}
                lauf={lauf}
                laufLaden={laufLaden}
                laufHistorie={laufHistorie}
                ankerFuerNeueListe={ankerFuerNeueListe}
                einreihen={(erfassung, laufId) =>
                  schlange.einreihen(
                    erstelleAuftrag(
                      erfassung,
                      kontext,
                      new Date(),
                      anmeldung?.name ?? null,
                      neueVorgangsId,
                      null,
                      laufId,
                    ),
                  )
                }
                finde={schlange.finde}
                bearbeitbar={listeBearbeitbar(listenSperre)}
                ladeVersion={ladeVersion}
              />
            )}
            {ansicht === 'suche' && <SucheAnsicht api={api} online={online} />}
            {ansicht === 'bestand' && <BestandAnsicht api={api} online={online} />}
            {ansicht === 'buchungen' && (
              <LagerBuchungen
                auftraege={schlange.auftraege}
                sendet={schlange.sendet}
                dauerhaft={schlange.dauerhaft}
                onSenden={(id) => void schlange.senden(id)}
                onVerwerfen={(a) => void schlange.verwerfen(a)}
                onFehlerZurueck={(a) => {
                  if (!listeBearbeitbar(listenSperre)) {
                    window.alert('Die Liste ist in einem anderen Fenster geöffnet.');
                    return;
                  }
                  const schl = listenSchluessel(unternehmerId);
                  speichereErfassung(
                    localStorage,
                    schl,
                    fehlerZurueckEinmal(
                      ladeErfassung(localStorage, schl),
                      a.inhalt.vorgangId,
                      fehlerzeilen(a),
                      a.inhalt.laufAnker,
                    ),
                  );
                  void schlange.fehlerUebernommen(a);
                  setAnsicht('scannen');
                }}
              />
            )}
            {ansicht === 'neu' &&
              (rechte.artikel ? (
                <>
                  <Vorschlaege
                    vorschlaege={vorschlaege}
                    gruppen={gruppen}
                    gruppenUst={gruppenUst}
                    mitEinstand={false}
                    uebernehmen={(id, daten) => api.vorschlagUebernehmen(id, daten)}
                    verwerfen={(id) => api.vorschlagVerwerfen(id)}
                    onGeaendert={() => void vorschlaegeLaden()}
                  />
                  {vorschlaege.length === 0 && (
                    <p className="hinweis">Keine offenen Vorschläge für neue Artikel.</p>
                  )}
                </>
              ) : (
                <div className="karte">
                  <p className="hinweis">
                    Neue Artikel anlegen darf nur, wer das Recht „Artikel bearbeiten“ hat.
                    {vorschlaege.length > 0 &&
                      ` ${String(vorschlaege.length)} Vorschlag/Vorschläge warten auf Übernahme.`}
                  </p>
                </div>
              ))}
            <p className="hinweis handterminal-fuss">
              Lagergerät · kein Verkauf ·{' '}
              <button
                type="button"
                className="taste zweit klein"
                onClick={() => {
                  if (
                    window.confirm(
                      zaehler.ausstehend > 0
                        ? `${String(zaehler.ausstehend)} Buchung(en) sind noch nicht gesendet und bleiben auf dem Gerät. Gerät trotzdem abmelden?`
                        : 'Gerät abmelden? Der Schlüssel wird vom Gerät entfernt.',
                    )
                  ) {
                    props.onAbmelden();
                  }
                }}
              >
                Gerät abmelden
              </button>
            </p>
          </main>
        </>
      )}
    </div>
  );
}

/** Scannen: Auskunft, Wareneingang oder Inventur. */
function ScanAnsicht(props: {
  api: LagergeraetApi;
  unternehmerId: string;
  online: boolean;
  darfBuchen: boolean;
  bediener: string | null;
  /** Offener Inventurlauf (Inventur zählt dann im Lauf) oder null. */
  lauf: GeraeteInventurlauf | null;
  laufLaden: () => Promise<void>;
  /** Laufstand frisch vom Server; null ohne Verbindung (dann keine Inventur übergeben). */
  laufHistorie: () => Promise<InventurStand | null>;
  /** Laufanker für den ersten Scan einer neuen Liste (undefined = Stand nicht frisch bekannt). */
  ankerFuerNeueListe: () => LaufAnker | undefined;
  /**
   * Legt den Auftrag ab und liefert den abgelegten Inhalt (bei bekannter Kennung den früheren);
   * `laufId` nur bei Inventur im offenen Lauf.
   */
  einreihen: (e: Erfassung, laufId: number | null) => Promise<AuftragsInhalt>;
  finde: (vorgangId: string) => Promise<AuftragsInhalt | null>;
  /** Dieser Tab hält die Listensperre (sonst nur Auskunft). */
  bearbeitbar: boolean;
  /** Zählt hoch, wenn die Liste aus dem localStorage neu zu laden ist. */
  ladeVersion: number;
}) {
  const { api, unternehmerId } = props;
  const schl = listenSchluessel(unternehmerId);
  const [erfassung, setErfassung] = useState<Erfassung>(() =>
    mitKennung(ladeErfassung(localStorage, schl)),
  );
  const [modus, setModus] = useState<Modus>(() =>
    ladeErfassung(localStorage, schl).zeilen.length > 0
      ? ladeErfassung(localStorage, schl).art
      : 'auskunft',
  );
  const [code, setCode] = useState('');
  const [tastatur, setTastatur] = useState(false);
  const [kamera, setKamera] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [artikel, setArtikel] = useState<GeraeteArtikel | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const codeFeld = useRef<HTMLInputElement>(null);

  // Sperre hält das Gerät (Lagergeraet), auch während andere Ansichten offen sind. Bei
  // Erhalt der Sperre bzw. Änderung in einem anderen Tab (ohne Web Locks) neu laden.
  const gesperrt = !props.bearbeitbar;
  const { ladeVersion } = props;
  useEffect(() => {
    if (ladeVersion > 0) {
      setErfassung(mitKennung(ladeErfassung(localStorage, schl)));
    }
  }, [ladeVersion, schl]);
  // Im Render, in dem eine Neuladung angestoßen wird, nichts speichern – sonst landete kurz
  // der alte Stand im localStorage. Gespeichert wird erst der neu geladene Stand.
  const gespeicherteVersion = useRef(ladeVersion);
  useEffect(() => {
    if (gespeicherteVersion.current !== ladeVersion) {
      gespeicherteVersion.current = ladeVersion;
      return;
    }
    if (!gesperrt) {
      speichereErfassung(localStorage, schl, erfassung);
    }
  }, [schl, erfassung, gesperrt, ladeVersion]);

  // Liste schon übergeben (Absturz zwischen Ablage und Leeren, zweiter Tab)? Dann nur
  // den Rest behalten und eine neue Kennung vergeben – nie zweimal buchen.
  const { finde } = props;
  const listenKennung = erfassung.vorgangId;
  useEffect(() => {
    if (listenKennung === undefined) {
      return undefined;
    }
    let aktiv = true;
    void finde(listenKennung).then((a) => {
      if (aktiv && a !== null) {
        setErfassung((e) => (e.vorgangId === listenKennung ? listeNachUebergabe(e, a) : e));
      }
    });
    return () => {
      aktiv = false;
    };
  }, [finde, listenKennung]);

  // Scanner im Tastaturmodus: Fokus immer zurück ins Codefeld
  const fokus = useCallback(() => {
    codeFeld.current?.focus();
  }, []);
  useEffect(() => {
    fokus();
    const sichtbar = (): void => {
      if (document.visibilityState === 'visible') {
        fokus();
      }
    };
    document.addEventListener('visibilitychange', sichtbar);
    return () => {
      document.removeEventListener('visibilitychange', sichtbar);
    };
  }, [fokus, modus]);

  const erfasse = useCallback(
    async (roh: string) => {
      setMeldung(null);
      if (gesperrt && modus !== 'auskunft') {
        setFehler('Die Liste ist in einem anderen Fenster geöffnet – dort weiterarbeiten.');
        return;
      }
      const ean = pruefeCode(roh);
      if (ean === null) {
        setFehler(`„${roh}“ ist kein Code (1–48 Zeichen ohne Leerzeichen)`);
        return;
      }
      setFehler(null);
      let auskunft: Awaited<ReturnType<LagergeraetApi['codeAuskunft']>> | null = null;
      if (navigator.onLine) {
        try {
          auskunft = await api.codeAuskunft(ean);
        } catch {
          auskunft = null; // offline oder Zeitüberschreitung – beim Buchen geklärt
        }
      }
      if (auskunft?.status === 'mehrdeutig') {
        // Nur über die EAN-Normalisierung, aber mehrere Artikel: nichts zuordnen
        setArtikel(null);
        setFehler(
          `${ean} passt zu mehreren Artikeln (${(auskunft.mehrdeutig ?? [])
            .map((m) => m.bezeichnung)
            .join(', ')}) – Artikel über „Suche“ wählen oder den Code im Backoffice bereinigen`,
        );
        return;
      }
      // A5-6a: falsche GTIN-Prüfziffer vorab melden (nicht als „neu“ in die Liste)
      const einwand = modus === 'auskunft' ? null : pruefzifferEinwand(ean, auskunft);
      if (einwand?.art === 'abweisen') {
        setArtikel(null);
        setFehler(einwand.text);
        return;
      }
      if (modus === 'auskunft') {
        if (auskunft === null) {
          setArtikel(null);
          setFehler('Keine Auskunft möglich (offline?)');
        } else if (auskunft.artikel === null) {
          setArtikel(null);
          setMeldung(
            `${ean}: unbekannt${auskunft.vorschlag === null ? '' : ' – als Vorschlag erfasst'}${
              auskunft.produkt === null ? '' : ` (${auskunft.produkt.bezeichnung})`
            }`,
          );
        } else {
          setArtikel(auskunft.artikel);
        }
        return;
      }
      const a = auskunft?.artikel ?? null;
      const zeile = {
        code: ean,
        bezeichnung:
          a?.bezeichnung ??
          auskunft?.vorschlag?.bezeichnung ??
          auskunft?.produkt?.bezeichnung ??
          '',
        bekannt: a !== null,
        bestand: a?.bestand ?? null,
        sperrgrund: a?.gesperrt === true ? (a.sperrgrund ?? 'gesperrt') : null,
      };
      setArtikel(a);
      const anker = props.ankerFuerNeueListe();
      setErfassung((e) => scanHinzufuegen(e, zeile, 1, anker));
      if (einwand !== null) {
        setFehler(einwand.text);
      }
    },
    [api, modus, gesperrt, props],
  );

  function modusWechseln(m: Modus): void {
    if (m === modus) {
      return;
    }
    if (
      m !== 'auskunft' &&
      erfassung.zeilen.length > 0 &&
      m !== erfassung.art &&
      !window.confirm('Die Liste ist noch nicht gebucht. Trotzdem die Art wechseln?')
    ) {
      return;
    }
    setModus(m);
    setArtikel(null);
    setFehler(null);
    setMeldung(null);
    if (m === 'inventur') {
      // Beim Wechsel zur Inventur den Laufstand frisch holen
      void props.laufLaden();
    }
    if (m !== 'auskunft') {
      setErfassung((e) => ({ ...e, art: m }));
    }
  }

  // Eintrittssperre: ein zweiter Tipp während Prüfung oder Bestätigung startet nichts
  const buchenLaeuft = useRef(false);
  async function buchen(): Promise<void> {
    if (buchenLaeuft.current) {
      return;
    }
    buchenLaeuft.current = true;
    try {
      await buchenGesperrt();
    } finally {
      buchenLaeuft.current = false;
    }
  }

  async function buchenGesperrt(): Promise<void> {
    let imLauf: InventurStand['offen'] = null;
    if (erfassung.art === 'inventur') {
      setLaeuft(true);
      const stand = await props.laufHistorie();
      setLaeuft(false);
      if (stand === null) {
        setFehler(
          'Ob ein Inventurlauf offen ist, lässt sich gerade nicht prüfen (keine Verbindung). Die Liste bleibt gespeichert – mit Verbindung erneut übergeben.',
        );
        return;
      }
      const ziel = inventurZiel(stand, erfassung.laufAnker);
      if (ziel.art === 'gesperrt') {
        setFehler(ziel.grund);
        return;
      }
      imLauf = ziel.art === 'lauf' ? stand.offen : null;
    }
    if (imLauf !== null && erfassung.bezug.trim() === '') {
      setFehler('Zählstelle angeben (z. B. Regal 3) – im Inventurlauf zählt jede Stelle für sich');
      return;
    }
    if (
      erfassung.art === 'inventur' &&
      !window.confirm(
        imLauf === null
          ? 'Inventur buchen? Der Bestand jedes Artikels wird auf die gezählte Menge gesetzt.'
          : `Zählung der Zählstelle „${erfassung.bezug.trim()}“ an Inventurlauf ${String(imLauf.id)} übergeben? Den Bestand setzt erst der Abschluss im Backoffice.`,
      )
    ) {
      return;
    }
    setLaeuft(true);
    setFehler(null);
    try {
      if (gesperrt) {
        throw new Error('Die Liste ist in einem anderen Fenster geöffnet');
      }
      const liste = mitKennung(erfassung);
      // Liste samt Kennung sofort sichern, bevor der Auftrag abgelegt wird
      speichereErfassung(localStorage, schl, liste);
      const gespeichert = await props.einreihen(liste, imLauf?.id ?? null);
      // Genau die übergebenen Mengen abziehen; währenddessen Gescanntes bleibt stehen.
      setErfassung((e) =>
        e.vorgangId === liste.vorgangId ? listeNachUebergabe(e, gespeichert) : e,
      );
      setArtikel(null);
      setMeldung(
        navigator.onLine
          ? 'Übergeben – Stand unter „Buchungen“'
          : 'Offline gespeichert – wird bei Verbindung automatisch gebucht',
      );
    } catch (e: unknown) {
      setFehler(`Nicht gespeichert, die Liste bleibt: ${fehlertext(e)}`);
    } finally {
      setLaeuft(false);
      fokus();
    }
  }

  const liste = modus !== 'auskunft';
  const summe = erfassung.zeilen.reduce((s, z) => s + z.menge, 0);

  return (
    <>
      <div className="karte">
        <div className="chips handterminal-modus" role="group" aria-label="Was tun?">
          {(
            [
              ['auskunft', 'Auskunft'],
              ['wareneingang', 'Wareneingang'],
              ['inventur', 'Inventur'],
            ] as const
          ).map(([m, text]) => (
            <button
              key={m}
              type="button"
              className={modus === m ? 'aktiv' : ''}
              onClick={() => {
                modusWechseln(m);
              }}
            >
              {text}
            </button>
          ))}
        </div>
        <form
          className="handterminal-scan"
          onSubmit={(e) => {
            e.preventDefault();
            const c = code.trim();
            setCode('');
            if (c !== '') {
              void erfasse(c);
            }
          }}
        >
          <label className="scan-code">
            Code scannen
            <input
              ref={codeFeld}
              // Ohne Bildschirmtastatur: der Scanner tippt (Tastatur-Wedge, Suffix Enter)
              inputMode={tastatur ? 'numeric' : 'none'}
              autoComplete="off"
              enterKeyHint="go"
              value={code}
              placeholder="Scanner bereit – Enter übernimmt"
              onChange={(e) => {
                setCode(e.target.value);
              }}
            />
          </label>
          <div className="zeile">
            <button type="submit" className="taste zweit">
              OK
            </button>
            <button
              type="button"
              className="taste zweit"
              aria-pressed={tastatur}
              onClick={() => {
                setTastatur(!tastatur);
                fokus();
              }}
            >
              Tastatur
            </button>
            <button
              type="button"
              className="taste zweit"
              onClick={() => {
                setKamera(true);
              }}
            >
              Kamera
            </button>
          </div>
        </form>
        {gesperrt && (
          <p className="warnung">
            Die Liste ist in einem anderen Fenster geöffnet (oder wird gerade geladen) – hier nur
            Auskunft.
          </p>
        )}
        {fehler !== null && <p className="fehler">{fehler}</p>}
        {meldung !== null && <p className="hinweis">{meldung}</p>}
        {!props.online && (
          <p className="warnung">Offline: weiterscannen möglich, Auskunft und Bestand fehlen.</p>
        )}
      </div>

      {artikel !== null && <ArtikelKarte api={api} artikel={artikel} />}

      {liste && (
        <div className="karte">
          <h2>
            {erfassung.art === 'wareneingang' ? 'Wareneingang' : 'Inventur'} ·{' '}
            {erfassung.zeilen.length} Artikel · {mengeText(summe)} Einheiten
          </h2>
          <label>
            {bezugBeschriftung(erfassung.art)}
            <input
              value={erfassung.bezug}
              maxLength={200}
              placeholder={erfassung.art === 'wareneingang' ? 'z. B. LS-4711' : 'z. B. Regal 3'}
              onChange={(e) => {
                setErfassung({ ...erfassung, bezug: e.target.value });
              }}
            />
          </label>
          {erfassung.art === 'inventur' && props.lauf !== null && (
            <p className="warnung">
              Inventurlauf {props.lauf.id} „{props.lauf.bezeichnung}“ ist offen: Diese Liste zählt
              für die Zählstelle oben. Mehrere Zählstellen werden addiert; den Bestand setzt erst
              der Abschluss im Backoffice.
            </p>
          )}
          <p className="hinweis">
            {erfassung.art === 'wareneingang'
              ? 'Jeder Scan zählt 1 dazu; Menge mit − / + oder direkt ändern. Unbekannte Codes werden als neue Artikel vorgeschlagen.'
              : props.lauf !== null
                ? 'Mehrfachscans addieren. Zählstelle angeben (Pflicht), dann übergeben.'
                : 'Mehrfachscans addieren. Beim Buchen wird der Bestand auf die gezählte Menge gesetzt – erst buchen, wenn der Artikel vollständig gezählt ist.'}
          </p>
          {erfassung.zeilen.length === 0 ? (
            <p className="hinweis">Noch nichts erfasst.</p>
          ) : (
            <ul className="erfassungsliste">
              {erfassung.zeilen.map((z) => (
                <li key={z.code}>
                  <div className="erfassung-text">
                    <strong>{z.bezeichnung === '' ? 'unbekannt' : z.bezeichnung}</strong>
                    <span className="hinweis">
                      {z.code}
                      {z.bestand !== null && ` · Bestand ${mengeText(z.bestand)}`}
                      {!z.bekannt && (erfassung.art === 'wareneingang' ? ' · neu' : ' · unbekannt')}
                      {z.sperrgrund != null && ` · Verkauf gesperrt: ${z.sperrgrund}`}
                    </span>
                  </div>
                  <div className="erfassung-menge">
                    <button
                      type="button"
                      className="taste zweit"
                      aria-label={`${z.code} eins weniger`}
                      onClick={() => {
                        setErfassung((alt) =>
                          z.menge <= 1
                            ? zeileEntfernen(alt, z.code)
                            : mengeSetzen(alt, z.code, z.menge - 1),
                        );
                      }}
                    >
                      −
                    </button>
                    <input
                      inputMode="decimal"
                      aria-label={`Menge ${z.code}`}
                      defaultValue={mengeText(z.menge)}
                      key={`${z.code}-${String(z.menge)}`}
                      onBlur={(e) => {
                        const m = leseMenge(e.target.value);
                        if (m !== null) {
                          setErfassung((alt) =>
                            m === 0 && erfassung.art === 'wareneingang'
                              ? zeileEntfernen(alt, z.code)
                              : mengeSetzen(alt, z.code, m),
                          );
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="taste zweit"
                      aria-label={`${z.code} eins mehr`}
                      onClick={() => {
                        setErfassung((alt) => mengeSetzen(alt, z.code, z.menge + 1));
                      }}
                    >
                      +
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="zeile handterminal-aktionen">
            <button
              type="button"
              className="taste gross"
              disabled={laeuft || erfassung.zeilen.length === 0 || !props.darfBuchen}
              onClick={() => void buchen()}
            >
              {erfassung.art === 'wareneingang' ? 'Wareneingang buchen' : 'Inventur buchen'}
            </button>
            <button
              type="button"
              className="taste zweit gefahr"
              disabled={laeuft || erfassung.zeilen.length === 0}
              onClick={() => {
                if (window.confirm('Liste verwerfen? Nichts davon wird gebucht.')) {
                  setErfassung({ ...erfassung, bezug: '', zeilen: [] });
                }
              }}
            >
              Verwerfen
            </button>
          </div>
          {!props.darfBuchen && (
            <p className="warnung">
              {props.bediener === null
                ? 'Zum Buchen anmelden.'
                : `${props.bediener} hat kein Lagerrecht – Liste bleibt gespeichert.`}
            </p>
          )}
        </div>
      )}

      {kamera && (
        <KameraScanner
          onCode={(ean) => void erfasse(ean)}
          onSchliessen={() => {
            setKamera(false);
            fokus();
          }}
        />
      )}
    </>
  );
}

const BEWEGUNG_TEXT: Record<GeraeteBewegung['art'], string> = {
  wareneingang: 'Wareneingang',
  verkauf: 'Verkauf',
  storno: 'Storno',
  inventur: 'Inventur',
  korrektur: 'Korrektur',
};

/** Artikelinformation samt Bestand und – auf Wunsch – Bewegungshistorie. */
function ArtikelKarte(props: { api: LagergeraetApi; artikel: GeraeteArtikel }) {
  const { api, artikel } = props;
  const [bewegungen, setBewegungen] = useState<GeraeteBewegung[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => {
    setBewegungen(null);
    setFehler(null);
  }, [artikel.id]);
  const unter =
    artikel.bestand !== null &&
    artikel.mindestbestand !== null &&
    artikel.bestand < artikel.mindestbestand;
  return (
    <div className="karte artikel-karte">
      <h2>{artikel.bezeichnung}</h2>
      <dl className="artikel-daten">
        <dt>Bestand</dt>
        <dd className={unter ? 'fehler' : ''}>
          {artikel.bestand === null ? 'ohne Bestandsführung' : mengeText(artikel.bestand)}
          {artikel.mindestbestand !== null && ` (min. ${mengeText(artikel.mindestbestand)})`}
        </dd>
        <dt>Preis</dt>
        <dd>{formatiereEuro(artikel.bruttoCent)} €</dd>
        <dt>EAN</dt>
        <dd>{artikel.ean ?? '–'}</dd>
        {artikel.barcode != null && artikel.barcode !== artikel.ean && (
          <>
            <dt>Barcode</dt>
            <dd>{artikel.barcode}</dd>
          </>
        )}
        {artikel.artikelnummer != null && (
          <>
            <dt>Artikelnr.</dt>
            <dd>{artikel.artikelnummer}</dd>
          </>
        )}
        {artikel.gesperrt === true && (
          <>
            <dt>Verkauf</dt>
            <dd className="warnung">
              gesperrt: {artikel.sperrgrund ?? 'ohne Angabe'} (Lagerbuchung möglich)
            </dd>
          </>
        )}
        <dt>Gruppe</dt>
        <dd>{artikel.gruppe === '' ? '–' : artikel.gruppe}</dd>
      </dl>
      {artikel.bestandsfuehrung &&
        (bewegungen === null ? (
          <button
            type="button"
            className="taste zweit"
            onClick={() => {
              api
                .bewegungen(artikel.id)
                .then((r) => {
                  setBewegungen(r.bewegungen);
                })
                .catch((e: unknown) => {
                  setFehler(fehlertext(e));
                });
            }}
          >
            Bewegungen anzeigen
          </button>
        ) : bewegungen.length === 0 ? (
          <p className="hinweis">Noch keine Bewegungen.</p>
        ) : (
          <ul className="bewegungsliste">
            {bewegungen.map((b) => (
              <li key={b.id}>
                <span>
                  {new Date(b.erstelltAm).toLocaleString('de-AT', {
                    timeZone: 'Europe/Vienna',
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}{' '}
                  {BEWEGUNG_TEXT[b.art]}
                  {b.bezug === null ? '' : ` · ${b.bezug}`}
                </span>
                <strong>
                  {b.menge > 0 ? '+' : ''}
                  {mengeText(b.menge)} → {mengeText(b.bestandNachher)}
                </strong>
              </li>
            ))}
          </ul>
        ))}
      {fehler !== null && <p className="fehler">{fehler}</p>}
    </div>
  );
}

/** Artikel ohne Scan finden (Bezeichnung oder EAN-Anfang). */
function SucheAnsicht(props: { api: LagergeraetApi; online: boolean }) {
  const [q, setQ] = useState('');
  const [treffer, setTreffer] = useState<GeraeteArtikel[] | null>(null);
  const [mehr, setMehr] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<GeraeteArtikel | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  async function suchen(): Promise<void> {
    if (q.trim().length < 2) {
      setFehler('Mindestens 2 Zeichen');
      return;
    }
    setFehler(null);
    try {
      const r = await props.api.suche(q.trim());
      setTreffer(r.treffer);
      setMehr(r.mehr);
      setGewaehlt(null);
    } catch (e: unknown) {
      setFehler(fehlertext(e));
    }
  }

  return (
    <>
      <div className="karte">
        <form
          className="handterminal-scan"
          onSubmit={(e) => {
            e.preventDefault();
            void suchen();
          }}
        >
          <label className="scan-code">
            Artikel suchen
            <input
              value={q}
              type="search"
              enterKeyHint="search"
              placeholder="Bezeichnung oder EAN"
              onChange={(e) => {
                setQ(e.target.value);
              }}
            />
          </label>
          <button type="submit" className="taste" disabled={!props.online}>
            Suchen
          </button>
        </form>
        {fehler !== null && <p className="fehler">{fehler}</p>}
        {treffer !== null && treffer.length === 0 && <p className="hinweis">Keine Treffer.</p>}
        {treffer !== null && treffer.length > 0 && (
          <ul className="trefferliste">
            {treffer.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className={gewaehlt?.id === t.id ? 'aktiv' : ''}
                  onClick={() => {
                    setGewaehlt(t);
                  }}
                >
                  <strong>{t.bezeichnung}</strong>
                  <span className="hinweis">
                    {t.ean ?? 'ohne EAN'} ·{' '}
                    {t.bestand === null ? 'ohne Bestand' : `Bestand ${mengeText(t.bestand)}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {mehr && <p className="hinweis">Weitere Treffer – Suche genauer fassen.</p>}
      </div>
      {gewaehlt !== null && <ArtikelKarte api={props.api} artikel={gewaehlt} />}
    </>
  );
}

/** Bestandsliste (Artikel mit Bestandsführung), seitenweise, optional nur unter Mindestbestand. */
function BestandAnsicht(props: { api: LagergeraetApi; online: boolean }) {
  const [nurUnter, setNurUnter] = useState(false);
  const [seite, setSeite] = useState<{
    artikel: BestandZeile[];
    gesamt: number;
    offset: number;
  } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(
    async (offset: number) => {
      try {
        setSeite(await props.api.bestand(offset, nurUnter));
        setFehler(null);
      } catch (e: unknown) {
        setFehler(fehlertext(e));
      }
    },
    [props.api, nurUnter],
  );
  useEffect(() => {
    void laden(0);
  }, [laden]);

  return (
    <div className="karte">
      <h2>Bestand</h2>
      <label>
        <input
          type="checkbox"
          checked={nurUnter}
          onChange={(e) => {
            setNurUnter(e.target.checked);
          }}
        />
        nur unter Mindestbestand
      </label>
      {!props.online && <p className="warnung">Offline – Stand vom letzten Abruf.</p>}
      {fehler !== null && <p className="fehler">{fehler}</p>}
      {seite !== null && (
        <>
          <ul className="bestandsliste">
            {seite.artikel.map((a) => (
              <li key={a.id} className={a.unterMindestbestand ? 'unter' : ''}>
                <span>
                  <strong>{a.bezeichnung}</strong>
                  <span className="hinweis"> {a.ean ?? ''}</span>
                </span>
                <strong>{mengeText(a.bestand)}</strong>
              </li>
            ))}
          </ul>
          <div className="zeile">
            <button
              type="button"
              className="taste zweit"
              disabled={seite.offset === 0}
              onClick={() => void laden(Math.max(0, seite.offset - 100))}
            >
              Zurück
            </button>
            <span className="hinweis">
              {seite.gesamt === 0
                ? 'keine Artikel'
                : `${String(seite.offset + 1)}–${String(seite.offset + seite.artikel.length)} von ${String(seite.gesamt)}`}
            </span>
            <button
              type="button"
              className="taste zweit"
              disabled={seite.offset + seite.artikel.length >= seite.gesamt}
              onClick={() => void laden(seite.offset + 100)}
            >
              Weiter
            </button>
          </div>
        </>
      )}
    </div>
  );
}
